import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

/**
 * CLE-179 — lightweight, dependency-free toast for the Linear task list.
 *
 * Linear surfaces a transient "Task XXX-123 moved to In Progress" toast after a
 * drag-and-drop status change. The app has no global toast system, so this is a
 * small self-contained provider rendered via a React portal (same approach as
 * the existing `Popover`). Toasts auto-dismiss after a few seconds and stack at
 * the bottom-right.
 */

/** An optional link shown after the message (e.g. "Vezi clientul"). */
interface ToastAction {
  label: string;
  href: string;
}

interface ToastItem {
  id: number;
  message: string;
  action?: ToastAction;
}

interface ToastApi {
  show: (message: string, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  // A no-op fallback keeps callers working if a provider isn't mounted (e.g.
  // in isolated tests) without throwing.
  return ctx ?? { show: () => {} };
}

const DISMISS_MS = 3200;
// A toast with a link stays longer, so there is time to click it.
const DISMISS_WITH_ACTION_MS = 6000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const show = useCallback((message: string, action?: ToastAction) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, message, action }]);
    window.setTimeout(
      () => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      },
      action ? DISMISS_WITH_ACTION_MS : DISMISS_MS,
    );
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-4 right-4 z-[80] flex flex-col items-end gap-2">
          {toasts.map((t) => (
            <ToastCard
              key={t.id}
              message={t.message}
              action={t.action}
              onDismiss={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
            />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastCard({
  message,
  action,
  onDismiss,
}: {
  message: string;
  action?: ToastAction;
  onDismiss: () => void;
}) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);

  return (
    <div
      role="status"
      onClick={onDismiss}
      className={`pointer-events-auto flex max-w-xs cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-gray-200 px-3 py-2 text-[13px] font-medium text-gray-800 shadow-lg transition-all duration-200 ${
        entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
    >
      <span>{message}</span>
      {action && (
        <Link
          to={action.href}
          onClick={onDismiss}
          className="shrink-0 font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-800"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}
